import React, { useContext, useMemo } from 'react';
import { emptyFinancials } from 'common/constants/calibration';
import { FinancialStatementsBasic } from 'common/types/financials';
import { ValuationsApproach } from 'common/types/valuation';
import { CustomSelect, withTooltip } from 'components';
import { OptionType } from 'components/CustomSelect/types';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import CalibrationContext from 'context/CalibrationContext';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import createColumnsCalibrationPerformance from 'pages/Valuations/approaches/guidelineCalibration/performance_metrics/config/createColumns/createColumnsCalibrationPerformance';
import rowConfigCalibrationPerformance from 'pages/Valuations/approaches/guidelineCalibration/performance_metrics/config/rowConfig/rowConfigCalibrationPerformance';
import ValuationContext from 'pages/ValuationsAllocation/ValuationContext';
import { getObjectValue } from 'utilities';
import rowTransformerCalibrationInputs from './createColumnsCalibrationInputs';
import getRowConfigCalibrationInputs from './rowConfigCalibrationInputs';
import { FinancialsTransposed, transposeFinancials } from '../../../../ValuationsAllocation/util';
import rowConfigCalibrationOutputs from '../Outputs/rowConfigCalibrationOutputs';
import rowTransformerCalibrationOutputs from '../Outputs/rowTransformerCalibrationOutputs';
import { getSelectorValues, mapPerformancesToGPCWithCapIQ } from '../utilities';

interface FinancialVersionsListProps {
  cell: ICell;
  options: FinancialStatementsBasic[];
  onCommit: () => void;
  onRevert: () => void;
}

const FinancialVersionsList = ({ cell, options, ...restProps }: FinancialVersionsListProps) => {
  const { onCommit, onRevert } = restProps;

  const { onCellsChanged } = useContext(FeaturedSpreadsheetContext);
  const {
    approaches,
    calibrationPerformanceSheet,
    calibrationOutputSheet,
    calibrationInputSheet,
    company,
    customClasses,
    financialStatementsList,
    calibration,
  } = useContext(CalibrationContext) as any;

  const parsedFinancialStatementsList = useMemo(() => financialStatementsList as FinancialStatementsBasic[], [financialStatementsList]);

  const parsedApproaches = useMemo(() => approaches as ValuationsApproach[], [approaches]);

  const { resetConfiguration } = useContext(ValuationContext);

  const handleChange = (value: string | null) => {
    const newValue = value ?? '';

    let financials: FinancialsTransposed = emptyFinancials;

    let isReadOnlyData = true;
    if (Number(newValue) === 0) {
      isReadOnlyData = false;
    } else {
      const selectedFinancialVersion = parsedFinancialStatementsList.filter(tab => tab.id === Number(newValue));

      financials = transposeFinancials(getObjectValue(selectedFinancialVersion[0]));
    }

    const approach = parsedApproaches.find(
      item =>
        item.panelId === (cell?.sheet?.tableData as any)?.calibration?.public_comps_approach_calibration
        ||item.panelId === (cell?.sheet?.tableData as any)?.calibration?.gpc_reference
    );
    const { valuations_approach_gpc: valuationsApproachGpc } = getObjectValue(approach);

    const rowConfigInputs = getRowConfigCalibrationInputs({
      financialStatementsList,
      readOnlyData: isReadOnlyData,
    });

    const columnsInputs = rowTransformerCalibrationInputs(financials);

    /* eslint-disable no-param-reassign */
    calibration.ltm_revenue_inputs = columnsInputs[0].ltm_revenue_inputs.value;
    calibration.ntm_revenue_inputs = columnsInputs[0].ntm_revenue_inputs.value;
    calibration.ltm_ebitda_inputs = columnsInputs[0].ltm_ebitda_inputs.value;
    calibration.ntm_ebitda_inputs = columnsInputs[0].ntm_ebitda_inputs.value;
    calibration.data_updated = false;
    (cell?.sheet?.tableData as any).financials = financials;

    calibrationInputSheet.reset({
      rowConfig: rowConfigInputs,
      columns: columnsInputs,
    });

    const { gpc_comparison: gpcComparison } = calibration.approach?.valuations_approach_gpc ?? {};
    const gpcModels = mapPerformancesToGPCWithCapIQ(calibration.performances || [], gpcComparison || []);

    const columns = {
      valuationsApproachGpc,
      financials,
      gpcComparisonByDate: gpcModels,
    };

    const rowConfig = rowConfigCalibrationPerformance({
      company,
      valuationsApproachGpc,
      gpcComparisonByDate: gpcModels,
    });

    const columnsPerformance = createColumnsCalibrationPerformance(columns);
    columnsPerformance.splice(0, 1);
    calibrationPerformanceSheet.reset({
      rowConfig,
      columns: columnsPerformance,
    });

    const rowConfigOutputs = rowConfigCalibrationOutputs({
      calibration,
      columnsPerformance,
    });

    const columnsOutputs = rowTransformerCalibrationOutputs(getSelectorValues(calibration));

    calibrationOutputSheet.reset({
      rowConfig: rowConfigOutputs,
      columns: columnsOutputs,
    });

    onCellsChanged([{ cell, value: newValue }]);

    resetConfiguration?.();
  };

  const getOptions = useMemo(() => {
    const tmpOptions: OptionType[] = [];
    if (Array.isArray(options)) {
      options.forEach(option => {
        const optionValue = option?.id?.toString() ?? '';
        tmpOptions.push({
          value: optionValue,
          label: option.name!,
        });
      });
      tmpOptions.push({
        value: '0',
        label: 'Specify Financials Metrics',
      });
    }

    return tmpOptions;
  }, [options]);

  return (
    <CustomSelect
      id={`financial-versions-list-${cell.key}`}
      value={cell.value as string}
      options={getOptions}
      onChange={handleChange}
      onCommit={onCommit}
      onRevert={onRevert}
      onEmptyValue="SELECT-OPTION"
      disabled={cell.readOnly}
    />
  );
};

export default withTooltip(FinancialVersionsList);
